import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Send, Sparkles, Clock, Phone, Loader2 } from "lucide-react";

const SESSION_KEY = "lsc_ai_session";

function ensureSession() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

const QUICK = [
  "What services do you offer?",
  "Show me membership options",
  "Can I book a chauffeur for tomorrow?",
  "What is the cheapest cleaning package?",
];

export default function Concierge() {
  const [sessionId] = useState(ensureSession);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get(`/ai/history/${sessionId}`).then((r) => setMessages(r.data)).catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const body = (text ?? input).trim();
    if (!body || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: body, id: "tmp-" + Date.now() }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId, message: body });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, id: "r-" + Date.now() }]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Concierge unavailable");
      setMessages((m) => [...m, { role: "assistant", content: "I'm briefly offline — please call +44 1234 567 890 or email info@luxorservicesconcierge.com and we'll respond right away.", id: "e-" + Date.now() }]);
    } finally {
      setSending(false);
    }
  };

  const empty = messages.length === 0;

  return (
    <div data-testid="concierge-page" className="bg-[#F5EFE4]">
      <section className="relative pt-32 pb-12 bg-[#1A1815] overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1522255272218-7ac5249be344?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1815] via-[#1A1815]/85 to-[#1A1815]/40" />
        <div className="relative z-10 max-w-[1100px] mx-auto px-6 lg:px-10">
          <p className="overline">AI Concierge · 24/7</p>
          <h1 className="font-serif text-5xl lg:text-6xl mt-4 leading-[1.02] text-[#F5EFE4] max-w-2xl">
            The <em className="italic text-[#C5A059]">Luxor</em> desk, always open.
          </h1>
          <p className="mt-6 text-[#F5EFE4]/70 max-w-lg">
            Ask about services, pricing, memberships, or make a booking request. A real
            concierge takes over during business hours if needed.
          </p>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 flex flex-col bg-white border border-[#E4D9C1] h-[70vh]" data-testid="concierge-chat">
          <div className="p-4 border-b border-[#E4D9C1] flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#C5A059] text-white flex items-center justify-center"><Sparkles size={16} /></div>
            <div>
              <p className="text-sm font-medium text-[#1A1815]">Luxor Concierge</p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#C5A059]">Online · 24/7</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4 bg-[#FAF6EE]">
            {empty ? (
              <div className="text-center max-w-md mx-auto mt-8">
                <p className="font-serif text-2xl text-[#1A1815]">How may we assist?</p>
                <p className="text-sm text-[#6B665E] mt-2">Ask anything about our services, pricing, or memberships.</p>
                <div className="mt-6 flex flex-col gap-2">
                  {QUICK.map((q) => (
                    <button key={q} onClick={() => send(q)} className="text-left text-sm px-4 py-3 bg-white border border-[#E4D9C1] hover:border-[#C5A059] transition-colors text-[#1A1815]" data-testid={`concierge-quick-${QUICK.indexOf(q)}`}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={m.id || i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#1A1815] text-[#F5EFE4]" : "bg-white border border-[#E4D9C1] text-[#1A1815]"}`}>
                    {m.content}
                  </div>
                </div>
              ))
            )}
            {sending && (
              <div className="flex justify-start">
                <div className="px-4 py-3 bg-white border border-[#E4D9C1] text-[#6B665E] flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Thinking…
                </div>
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-4 border-t border-[#E4D9C1] flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your message…" className="luxor-field flex-1" data-testid="concierge-input" disabled={sending} />
            <button type="submit" className="luxor-btn-primary shrink-0 disabled:opacity-50" disabled={sending || !input.trim()} data-testid="concierge-send">
              <Send size={14} />
            </button>
          </form>
        </div>

        <aside className="lg:col-span-4 space-y-4">
          <div className="bg-white border border-[#E4D9C1] p-6">
            <p className="overline">Prefer a person?</p>
            <ul className="mt-5 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Phone size={15} className="text-[#C5A059] mt-0.5" />
                <div>
                  <a href="tel:+441234567890" className="text-[#1A1815] hover:text-[#C5A059]">+44 1234 567 890</a>
                  <p className="text-xs text-[#6B665E]">Tap to call</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={15} className="text-[#C5A059] mt-0.5" />
                <div>
                  <p className="text-[#1A1815]">9am – 9pm · Mon – Sun</p>
                  <p className="text-xs text-[#6B665E]">Human concierge hours</p>
                </div>
              </li>
            </ul>
          </div>
          <div className="bg-[#1A1815] text-[#F5EFE4] p-6">
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#C5A059] font-semibold">Ready to book?</p>
            <p className="text-sm text-[#F5EFE4]/80 mt-3">Skip the chat and go straight to reservation.</p>
            <Link to="/book" className="luxor-btn-primary mt-4 w-full justify-center">Reserve a service</Link>
          </div>
        </aside>
      </section>
    </div>
  );
}
